import React, { useState, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import Link from '@tiptap/extension-link';
import Image from '@tiptap/extension-image';
import {
  X,
  Check,
  Loader2,
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  Link as LinkIcon,
  Image as ImageIcon,
  Heading1,
  Heading2,
  Eye
} from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { WALKTHROUGHS } from '../../data/walkthroughs';

interface KnowledgeArticle {
  id: string;
  title: string;
  excerpt: string;
  content: string;
  video_embed: string | null;
  category: string;
  is_published: boolean;
  walkthrough_token: string | null;
  walkthrough_title: string | null;
}

interface KnowledgeArticleEditorProps {
  article: KnowledgeArticle | null;
  onClose: () => void;
  onSave: () => void;
}

const CATEGORIES = [
  { value: 'campaigns', label: 'Campagnes' },
  { value: 'blogs', label: 'Blogs' },
  { value: 'mailinglist', label: 'Mailinglijst' },
  { value: 'social-media', label: 'Social Media' },
  { value: 'account', label: 'Account' },
  { value: 'credits', label: 'Credits' }
];

export function KnowledgeArticleEditor({ article, onClose, onSave }: KnowledgeArticleEditorProps) {
  const [formData, setFormData] = useState({
    title: article?.title || '',
    excerpt: article?.excerpt || '',
    content: article?.content || '',
    video_embed: article?.video_embed || '',
    category: article?.category || 'campaigns',
    is_published: article?.is_published || false,
    walkthrough_token: article?.walkthrough_token || '',
    walkthrough_title: article?.walkthrough_title || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkUrl, setLinkUrl] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [imageUrl, setImageUrl] = useState('');

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3],
        },
      }),
      Underline,
      Link.configure({
        openOnClick: false,
        HTMLAttributes: {
          class: 'text-blue-600 underline',
        },
      }),
      Image.configure({
        inline: true,
        allowBase64: false,
        HTMLAttributes: {
          class: 'max-w-full h-auto rounded-lg',
        },
      }),
    ],
    content: formData.content,
    editorProps: {
      attributes: {
        class: 'prose prose-sm max-w-none focus:outline-none min-h-[300px] p-4 border border-gray-200 rounded-lg',
      },
    },
    onUpdate: ({ editor }) => {
      setFormData(prev => ({ ...prev, content: editor.getHTML() }));
    },
  });

  const handleSave = async () => {
    if (!formData.title.trim()) {
      setError('Titel is verplicht');
      return;
    }

    if (!formData.excerpt.trim()) {
      setError('Samenvatting is verplicht');
      return;
    }

    if (!formData.content.trim() || formData.content === '<p></p>') {
      setError('Artikel inhoud is verplicht');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      const { data: { user } } = await supabase.auth.getUser();

      const { data: adminUser } = await supabase
        .from('admin_users')
        .select('id')
        .eq('auth_user_id', user?.id)
        .maybeSingle();

      const articleData = {
        title: formData.title.trim(),
        excerpt: formData.excerpt.trim(),
        content: formData.content,
        video_embed: formData.video_embed.trim() || null,
        category: formData.category,
        is_published: formData.is_published,
        source_type: 'manual',
        walkthrough_token: formData.walkthrough_token || null,
        walkthrough_title: formData.walkthrough_title || null,
        ...(article ? { updated_by: adminUser?.id } : { created_by: adminUser?.id })
      };

      let savedArticleId: string;

      if (article) {
        const { error } = await supabase
          .from('chat_knowledge_base')
          .update(articleData)
          .eq('id', article.id);

        if (error) throw error;
        savedArticleId = article.id;
      } else {
        const { data: newArticle, error } = await supabase
          .from('chat_knowledge_base')
          .insert([articleData])
          .select('id')
          .single();

        if (error) throw error;
        if (!newArticle) throw new Error('Artikel aangemaakt maar geen ID ontvangen');
        savedArticleId = newArticle.id;
      }

      onSave();
    } catch (err: any) {
      console.error('Error saving article:', err);
      setError(err.message || 'Fout bij opslaan');
    } finally {
      setSaving(false);
    }
  };

  const insertLink = () => {
    if (!linkUrl) return;

    if (editor) {
      editor.chain().focus().setLink({ href: linkUrl }).run();
      setShowLinkModal(false);
      setLinkUrl('');
    }
  };

  const insertImage = () => {
    if (!imageUrl) return;

    if (editor) {
      editor.chain().focus().setImage({ src: imageUrl }).run();
      setShowImageModal(false);
      setImageUrl('');
    }
  };

  if (!editor) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl max-w-5xl w-full my-8 shadow-2xl max-h-[90vh] overflow-y-auto flex flex-col">
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white rounded-t-xl z-10">
          <h2 className="text-2xl font-bold text-gray-900">
            {article ? 'Artikel bewerken' : 'Nieuw artikel'}
          </h2>
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.is_published}
                onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                className="sr-only peer"
              />
              <div className="relative w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green-600"></div>
              <span className="text-sm font-medium text-gray-700 flex items-center gap-1.5">
                <Eye className="w-4 h-4" />
                Gepubliceerd
              </span>
            </label>
            <div className="w-px h-8 bg-gray-300"></div>
            <button
              onClick={onClose}
              disabled={saving}
              className="px-5 py-2.5 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 font-medium"
            >
              Annuleren
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2 font-medium shadow-sm"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Opslaan...
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  Opslaan
                </>
              )}
            </button>
          </div>
        </div>

        {error && (
          <div className="mx-6 mt-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center gap-2 text-red-700">
            <X className="w-5 h-5" />
            {error}
          </div>
        )}

        <div className="p-6 space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Titel *
              </label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                placeholder="Hoe maak ik een campagne aan?"
              />
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Categorie *
              </label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                {CATEGORIES.map(cat => (
                  <option key={cat.value} value={cat.value}>
                    {cat.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Samenvatting * <span className="text-gray-500 font-normal">(wordt getoond in lijstweergave)</span>
            </label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              rows={3}
              maxLength={200}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              placeholder="Korte samenvatting van het artikel (max 200 karakters)"
            />
            <p className="text-xs text-gray-500 mt-1.5">
              {formData.excerpt.length}/200 karakters
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Artikel inhoud *
            </label>

            <div className="border border-gray-300 rounded-lg overflow-hidden">
              <div className="bg-gray-50 border-b border-gray-200 p-2 flex flex-wrap gap-1 sticky top-[73px] z-10">
                <button
                  onClick={() => editor.chain().focus().toggleBold().run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bold') ? 'bg-gray-300' : ''}`}
                  title="Vet"
                >
                  <Bold className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleItalic().run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('italic') ? 'bg-gray-300' : ''}`}
                  title="Cursief"
                >
                  <Italic className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleUnderline().run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('underline') ? 'bg-gray-300' : ''}`}
                  title="Onderstreept"
                >
                  <UnderlineIcon className="w-4 h-4" />
                </button>
                <div className="w-px bg-gray-300 my-1" />
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 1 }) ? 'bg-gray-300' : ''}`}
                  title="Kop 1"
                >
                  <Heading1 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('heading', { level: 2 }) ? 'bg-gray-300' : ''}`}
                  title="Kop 2"
                >
                  <Heading2 className="w-4 h-4" />
                </button>
                <div className="w-px bg-gray-300 my-1" />
                <button
                  onClick={() => editor.chain().focus().toggleBulletList().run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('bulletList') ? 'bg-gray-300' : ''}`}
                  title="Opsomming"
                >
                  <List className="w-4 h-4" />
                </button>
                <button
                  onClick={() => editor.chain().focus().toggleOrderedList().run()}
                  className={`p-2 rounded hover:bg-gray-200 ${editor.isActive('orderedList') ? 'bg-gray-300' : ''}`}
                  title="Genummerde lijst"
                >
                  <ListOrdered className="w-4 h-4" />
                </button>
                <div className="w-px bg-gray-300 my-1" />
                <button
                  onClick={() => setShowLinkModal(true)}
                  className="p-2 rounded hover:bg-gray-200"
                  title="Link toevoegen"
                >
                  <LinkIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setShowImageModal(true)}
                  className="p-2 rounded hover:bg-gray-200"
                  title="Afbeelding toevoegen"
                >
                  <ImageIcon className="w-4 h-4" />
                </button>
              </div>

              <EditorContent editor={editor} />
            </div>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Rondleiding <span className="text-gray-500 font-normal">(optioneel)</span>
            </label>
            <select
              value={formData.walkthrough_token}
              onChange={(e) => {
                const token = e.target.value;
                const match = WALKTHROUGHS.find((w) => w.token === token);
                setFormData({
                  ...formData,
                  walkthrough_token: token,
                  walkthrough_title: match ? match.title : ''
                });
              }}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Geen rondleiding</option>
              {WALKTHROUGHS.map((w) => (
                <option key={w.token} value={w.token}>{w.title}</option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1.5">
              Koppel een uitlegvideo aan dit artikel. Wordt getoond bovenaan het artikel en aanbevolen in de chat.
            </p>
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Externe video embed <span className="text-gray-500 font-normal">(optioneel)</span>
            </label>
            <textarea
              value={formData.video_embed}
              onChange={(e) => setFormData({ ...formData, video_embed: e.target.value })}
              rows={3}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm resize-none"
              placeholder='<iframe src="https://www.youtube.com/embed/..." ...></iframe>'
            />
            <p className="text-xs text-gray-500 mt-1.5">
              Plak de volledige embed code van YouTube, Vimeo, etc. Een gekoppelde rondleiding heeft voorrang op deze embed.
            </p>
          </div>

        </div>
      </div>

      {showLinkModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Link toevoegen</h3>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://example.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowLinkModal(false);
                  setLinkUrl('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Annuleren
              </button>
              <button
                onClick={insertLink}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Invoegen
              </button>
            </div>
          </div>
        </div>
      )}

      {showImageModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60]">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Afbeelding toevoegen</h3>
            <input
              type="url"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/image.jpg"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent mb-4"
              autoFocus
            />
            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowImageModal(false);
                  setImageUrl('');
                }}
                className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg"
              >
                Annuleren
              </button>
              <button
                onClick={insertImage}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Invoegen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
